import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileToDataUrl } from "../../lib/admin";

const TYPES = [
  { value: "firmware", label: "Firmware" },
  { value: "calibration", label: "Calibration File" },
  { value: "software", label: "Software" },
  { value: "driver", label: "Driver" },
  { value: "other", label: "Other" },
];

const empty = {
  title: "",
  type: "firmware",
  brand_id: "",
  product_id: "",
  version: "",
  file_url: "",
  file_data: "",
  thumbnail: "",
  group: "",
};

export default function AdminDownloads() {
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () =>
    Promise.all([api.get("/downloads"), api.get("/brands"), api.get("/products")]).then(([d, b, p]) => {
      setItems(d.data);
      setBrands(b.data);
      setProducts(p.data);
    });
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing("new");
    setForm({ ...empty, brand_id: brands[0]?.id || "" });
  };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({ ...empty, ...d, product_id: d.product_id || "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, product_id: form.product_id || null };
      if (editing === "new") await api.post("/downloads", payload);
      else await api.put(`/downloads/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/downloads/${d.id}`);
    toast.success("Deleted");
    load();
  };

  const onFile = async (field, file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setForm({ ...form, [field]: url });
  };

  const brandName = (id) => brands.find((b) => b.id === id)?.name || "—";
  const productName = (id) => products.find((p) => p.id === id)?.name || "—";
  const filteredProducts = products.filter((p) => p.brand_id === form.brand_id);

  return (
    <div data-testid="admin-downloads-page">
      <div className="flex justify-between items-center mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Support</div>
          <h1 className="font-display text-4xl tracking-tighter">Downloads</h1>
        </div>
        <button onClick={openCreate} disabled={brands.length === 0} className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 disabled:opacity-50" data-testid="download-create-btn">
          <Plus size={14} /> New Download
        </button>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.2em] text-zinc-500">
              <th className="px-4 py-3 font-normal">Title</th>
              <th className="px-4 py-3 font-normal">Type</th>
              <th className="px-4 py-3 font-normal">Group</th>
              <th className="px-4 py-3 font-normal">Brand</th>
              <th className="px-4 py-3 font-normal">Product</th>
              <th className="px-4 py-3 font-normal">Version</th>
              <th className="px-4 py-3 font-normal">Source</th>
              <th className="px-4 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-white/5" data-testid={`download-row-${d.id}`}>
                <td className="px-4 py-3 font-medium">{d.title}</td>
                <td className="px-4 py-3 text-zinc-400">{d.type}</td>
                <td className="px-4 py-3 text-zinc-400">{d.group || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{brandName(d.brand_id)}</td>
                <td className="px-4 py-3 text-zinc-400">{d.product_id ? productName(d.product_id) : "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{d.version || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{d.file_url ? "URL" : "File"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(d)} className="text-zinc-400 hover:text-white p-2"><Edit2 size={14} /></button>
                  <button onClick={() => remove(d)} className="text-zinc-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No downloads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-[#0A0A0A] border border-white/10 p-6 my-8">
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl">{editing === "new" ? "New Download" : "Edit Download"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title" full>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="adm-input" data-testid="download-form-title" />
              </Field>
              <Field label="Type">
                <select required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="adm-input" data-testid="download-form-type">
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="Version (e.g. v1.2.0)">
                <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="adm-input" />
              </Field>
              <Field label="Brand">
                <select required value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value, product_id: "" })} className="adm-input">
                  <option value="">Select</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Product (optional)">
                <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="adm-input">
                  <option value="">— None —</option>
                  {filteredProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Group (e.g. E-mount Lens)" full>
                <input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className="adm-input" placeholder="e.g. E-mount Lens, Z-mount Lens" />
              </Field>
              <Field label="External File URL (optional)" full>
                <input value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className="adm-input" placeholder="https://..." />
              </Field>
              <Field label="OR Upload File" full>
                <input type="file" onChange={(e) => onFile("file_data", e.target.files?.[0])} className="text-xs text-zinc-400" />
                {form.file_data && <div className="text-xs text-emerald-400 mt-2">File loaded.</div>}
              </Field>
              <Field label="Thumbnail Image">
                <div className="flex items-center gap-3">
                  {form.thumbnail && <img src={form.thumbnail} className="h-10 w-16 object-cover" alt="" />}
                  <input type="file" accept="image/*" onChange={(e) => onFile("thumbnail", e.target.files?.[0])} className="text-xs text-zinc-400" />
                </div>
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] border border-white/10 hover:bg-white/5">Cancel</button>
              <button type="submit" className="brand-btn px-5 py-2.5 text-xs uppercase tracking-[0.2em]" data-testid="download-form-save">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.adm-input { width: 100%; background: #141414; border: 1px solid rgba(255,255,255,0.1); padding: 0.625rem 0.875rem; font-size: 0.875rem; color: white; outline: none; } .adm-input:focus { border-color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
